/**
 * Who is signed in, and which tenant they are looking at.
 *
 * The session is a react-query query rather than ad-hoc state so that a 401
 * anywhere in the app can invalidate it and every screen reacts at once. The
 * chosen tenant is kept in localStorage, wrapped in try/catch: a private
 * window, cleared site data or a browser blocking storage must degrade to "ask
 * again", never to a crash on boot.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qk, ApiError, type Membership, type MeResponse, type Role } from './api.ts';

const TENANT_KEY = 'sp.tenant';
const THEME_KEY = 'sp.theme';

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable; the session simply does not persist across reloads */
  }
}

export interface SessionValue {
  me: MeResponse | null;
  /** True while the very first `/api/me` is in flight. */
  loading: boolean;
  /** Set when the server could not be reached at all, so screens can say so. */
  offline: boolean;
  memberships: Membership[];
  tenant: Membership | null;
  role: Role | null;
  isBoss: boolean;
  isOwner: boolean;
  isDev: boolean;
  selectTenant: (tenantSlug: string) => void;
  setSession: (me: MeResponse) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [tenantSlug, setTenantSlug] = useState<string | null>(() => readStored(TENANT_KEY));

  const query = useQuery({
    queryKey: qk.me,
    queryFn: api.me,
    retry: (count, err) => !(err instanceof ApiError && err.isAuth) && count < 2,
    staleTime: 60_000,
  });

  const me = query.data ?? null;
  const memberships = useMemo(() => me?.memberships ?? [], [me]);

  // A stored tenant the account no longer belongs to (removed from the team, or
  // a different person signing in on the same device) must not stick.
  const tenant = useMemo(() => {
    if (memberships.length === 0) return null;
    const chosen = memberships.find((m) => m.tenantSlug === tenantSlug);
    if (chosen) return chosen;
    return memberships.length === 1 ? (memberships[0] ?? null) : null;
  }, [memberships, tenantSlug]);

  useEffect(() => {
    if (tenant && tenant.tenantSlug !== tenantSlug) {
      setTenantSlug(tenant.tenantSlug);
      writeStored(TENANT_KEY, tenant.tenantSlug);
    }
  }, [tenant, tenantSlug]);

  const selectTenant = useCallback((slug: string) => {
    setTenantSlug(slug);
    writeStored(TENANT_KEY, slug);
  }, []);

  const setSession = useCallback(
    (next: MeResponse) => {
      qc.setQueryData(qk.me, next);
      if (next.memberships.length === 1) {
        const only = next.memberships[0];
        if (only) selectTenant(only.tenantSlug);
      }
    },
    [qc, selectTenant],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // A failed logout call still clears the client. The cookie is HttpOnly so
      // the server is the only one who can really drop it, but leaving a stale
      // session on screen is worse than an optimistic sign out.
    }
    writeStored(TENANT_KEY, null);
    setTenantSlug(null);
    qc.clear();
    qc.setQueryData(qk.me, null);
  }, [qc]);

  const role = tenant?.role ?? null;

  const value: SessionValue = {
    me,
    loading: query.isLoading,
    offline: query.error instanceof ApiError && query.error.isOffline,
    memberships,
    tenant,
    role,
    // A dev is Sneaky Panda support: owner level power for support work, but
    // never a person the business rosters or pays.
    isBoss: role === 'owner' || role === 'manager' || role === 'dev',
    isOwner: role === 'owner' || role === 'dev',
    isDev: role === 'dev',
    selectTenant,
    setSession,
    signOut,
    refresh: () => query.refetch(),
  };

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession must be used inside SessionProvider');
  return v;
}

/**
 * The tenant slug for screens that only render once a tenant is chosen. Throws
 * rather than returning null so a page cannot silently build URLs against
 * "undefined".
 */
export function useTenantSlug(): string {
  const { tenant } = useSession();
  if (!tenant) throw new Error('No tenant selected');
  return tenant.tenantSlug;
}

// ---------------------------------------------------------------------------
// Theme. Three states: light, dark, and "follow the system", which stamps
// nothing on the root element and lets prefers-color-scheme decide.
// ---------------------------------------------------------------------------
export type ThemeChoice = 'light' | 'dark' | 'system';

export function readTheme(): ThemeChoice {
  const v = readStored(THEME_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

export function applyTheme(choice: ThemeChoice): void {
  const root = document.documentElement;
  if (choice === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', choice);
  writeStored(THEME_KEY, choice === 'system' ? null : choice);
}
